import type { VehicleSocket } from "../../../store/admin/vehiclesSocketStore";
import { isStatusId, type StatusId } from "../../lite/components/Sidebar/statusMeta";

export interface ShipmentUnit {
  deviceId: string;
  vehicleId: string;
  name: string;
  plate: string;
  driver: string;
  status: StatusId;
  latitude: number | null;
  longitude: number | null;
  speed: number | null;
  signal: number | null;
  battery: number | null;
  address: string;
  lastReportSeconds: number;
  iconId: string | null;
}

export interface UnitOption {
  value: string;
  label: string;
}

const coordinate = (value: number | null | undefined): number | null =>
  typeof value === "number" && Number.isFinite(value) && value !== 0 ? value : null;

export const findShipmentVehicle = (
  vehicles: readonly VehicleSocket[],
  deviceId: string | null | undefined,
): VehicleSocket | null =>
  deviceId ? (vehicles.find((item) => item.id === deviceId) ?? null) : null;

export const resolveShipmentUnit = (
  vehicles: readonly VehicleSocket[],
  deviceId: string | null | undefined,
): ShipmentUnit | null => {
  const vehicle = findShipmentVehicle(vehicles, deviceId);
  if (!vehicle) return null;

  return {
    deviceId: vehicle.id,
    vehicleId: vehicle.vehicle_id || "",
    name: vehicle.vehicle?.name || vehicle.fullId || vehicle.id,
    plate: vehicle.vehicle?.plates || "",
    driver: vehicle.driver?.name || "",
    status: isStatusId(vehicle.status) ? vehicle.status : "offline",
    latitude: coordinate(vehicle.latitude),
    longitude: coordinate(vehicle.longitude),
    speed: typeof vehicle.speed === "number" ? vehicle.speed : null,
    signal: typeof vehicle.signal === "number" ? vehicle.signal : null,
    battery: typeof vehicle.battery === "number" ? vehicle.battery : null,
    address: vehicle.address || "",
    lastReportSeconds: vehicle.lastReport?._seconds ?? 0,
    iconId: vehicle.icon_id ?? null,
  };
};

export const buildUnitOptions = (
  vehicles: readonly VehicleSocket[],
): UnitOption[] =>
  vehicles
    .map((vehicle) => {
      const name = vehicle.vehicle?.name || vehicle.fullId || vehicle.id;
      const plate = vehicle.vehicle?.plates;
      return { value: vehicle.id, label: plate ? `${name} (${plate})` : name };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

export const shipmentUpdateErrorKey = (
  error: unknown,
): "forbidden" | "notFound" | "generic" => {
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  if (status === 403) return "forbidden";
  if (status === 404) return "notFound";
  return "generic";
};
